import {
  createUserWithEmailAndPassword,
  getAuth,
  getRedirectResult,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithRedirect,
  signOut as firebaseSignOut,
  type User,
} from "firebase/auth";
import { type FormEvent, type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleMark } from "@/components/google-mark";
import { AuthContext, type AuthContextValue } from "@/src/auth-context";
import { authErrorMessage } from "@/src/auth-errors";
import { firebaseApp } from "@/src/firebase";
const auth = getAuth(firebaseApp);
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: "select_account" });
const GOOGLE_DESTINATION_KEY = "chuno:google-auth-destination";
const redirectResult = getRedirectResult(auth);

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [destination, setDestination] = useState<string | undefined>();
  const [redirectError, setRedirectError] = useState("");

  useEffect(() => onAuthStateChanged(auth, (nextUser) => {
    setUser(nextUser);
    setLoading(false);
  }), []);

  useEffect(() => {
    const fallback = window.setTimeout(() => {
      setUser(auth.currentUser);
      setLoading(false);
    }, 1500);
    return () => window.clearTimeout(fallback);
  }, []);

  useEffect(() => {
    let active = true;
    void redirectResult.then((result) => {
      if (!active || !result?.user) return;
      const storedDestination = sessionStorage.getItem(GOOGLE_DESTINATION_KEY);
      sessionStorage.removeItem(GOOGLE_DESTINATION_KEY);
      if (storedDestination?.startsWith("/")) navigate(storedDestination, { replace: true });
    }).catch((caught) => {
      if (!active) return;
      sessionStorage.removeItem(GOOGLE_DESTINATION_KEY);
      setRedirectError(authErrorMessage(caught));
      setDialogOpen(true);
    });
    return () => {
      active = false;
    };
  }, [navigate]);

  const openLogin = useCallback((nextDestination?: string) => {
    setRedirectError("");
    setDestination(nextDestination);
    setDialogOpen(true);
  }, []);

  const beginGoogleSignIn = useCallback(async (requestedDestination?: string) => {
    const nextDestination = requestedDestination || destination || `${window.location.pathname}${window.location.search}`;
    sessionStorage.setItem(GOOGLE_DESTINATION_KEY, nextDestination);
    await signInWithRedirect(auth, googleProvider);
  }, [destination]);

  const completeLogin = useCallback(() => {
    setDialogOpen(false);
    if (destination) navigate(destination);
    setDestination(undefined);
  }, [destination, navigate]);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    loading,
    openLogin,
    signInWithEmail: async (email, password) => {
      await signInWithEmailAndPassword(auth, email, password);
    },
    createAccount: async (email, password) => {
      await createUserWithEmailAndPassword(auth, email, password);
    },
    signInWithGoogle: beginGoogleSignIn,
    signOut: () => firebaseSignOut(auth),
  }), [beginGoogleSignIn, loading, openLogin, user]);

  return (
    <AuthContext.Provider value={value}>
      {children}
      {dialogOpen ? <LoginDialog initialError={redirectError} onGoogleSignIn={() => beginGoogleSignIn()} onClose={() => setDialogOpen(false)} onComplete={completeLogin} /> : null}
    </AuthContext.Provider>
  );
}

function LoginDialog({ initialError, onGoogleSignIn, onClose, onComplete }: { initialError: string; onGoogleSignIn: () => Promise<void>; onClose: () => void; onComplete: () => void }) {
  const [mode, setMode] = useState<"signin" | "create">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(initialError);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [busy, onClose]);

  const run = async (action: () => Promise<unknown>, complete = true) => {
    setBusy(true);
    setError("");
    try {
      await action();
      if (complete) onComplete();
    } catch (caught) {
      setError(authErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  const submitEmail = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void run(() => mode === "signin"
      ? signInWithEmailAndPassword(auth, email.trim(), password)
      : createUserWithEmailAndPassword(auth, email.trim(), password));
  };

  return (
    <div className="auth-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !busy) onClose();
    }}>
      <section className="auth-dialog" role="dialog" aria-modal="true" aria-labelledby="auth-title">
        <button className="auth-dialog__close" type="button" aria-label="Close sign in" onClick={onClose} disabled={busy}>×</button>
        <p className="auth-dialog__eyebrow">Chuno account</p>
        <h2 id="auth-title">{mode === "signin" ? "Sign in to apply." : "Create your account."}</h2>
        <p className="auth-dialog__intro">Sign in securely to continue with your Chuno application.</p>

        <button className="auth-google" type="button" disabled={busy} onClick={() => void run(onGoogleSignIn, false)}>
          <GoogleMark /> Continue with Google
        </button>

        <div className="auth-divider"><span>or use email</span></div>

        <form className="auth-form" onSubmit={submitEmail}>
          <label>
            <span>Email address</span>
            <input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required disabled={busy} autoFocus />
          </label>
          <label>
            <span>Password</span>
            <input type="password" autoComplete={mode === "signin" ? "current-password" : "new-password"} value={password} onChange={(event) => setPassword(event.target.value)} minLength={6} required disabled={busy} />
          </label>
          {error ? <p className="auth-error" role="alert">{error}</p> : null}
          <button className="button button--dark auth-submit" type="submit" disabled={busy}>
            {busy ? "Please wait…" : mode === "signin" ? "Sign in" : "Create account"}
          </button>
        </form>

        <p className="auth-switch">
          {mode === "signin" ? "New to Chuno?" : "Already have an account?"}{" "}
          <button type="button" onClick={() => {
            setMode((current) => current === "signin" ? "create" : "signin");
            setError("");
          }} disabled={busy}>{mode === "signin" ? "Create account" : "Sign in"}</button>
        </p>
      </section>
    </div>
  );
}
